import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MotifIcon,
  MotifVerticalNavigation,
  MotifVerticalNavigationMenu,
  MotifVerticalNavigationMenuItem,
} from '@ey-xd/motif-wc-react';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { IconoirHomeSimple } from '@ey-xd/motif-icon/Icons/Iconoir/HomeSimple.js';
import { IconoirTaskList } from '@ey-xd/motif-icon/Icons/Iconoir/TaskList.js';
import { IconoirMultiplePages } from '@ey-xd/motif-icon/Icons/Iconoir/MultiplePages.js';
import { IconoirStatsReport } from '@ey-xd/motif-icon/Icons/Iconoir/StatsReport.js';
import { IconoirDatabase } from '@ey-xd/motif-icon/Icons/Iconoir/Database.js';
import { IconoirViewGrid } from '@ey-xd/motif-icon/Icons/Iconoir/ViewGrid.js';
import { IconoirSettings } from '@ey-xd/motif-icon/Icons/Iconoir/Settings.js';
import { IconoirNavArrowLeft } from '@ey-xd/motif-icon/Icons/Iconoir/NavArrowLeft.js';
import { IconoirNavArrowRight } from '@ey-xd/motif-icon/Icons/Iconoir/NavArrowRight.js';
import { eventsModule } from '@/events/events-module';
import { getTracker } from '@/telemetry/tracker';
import { useClientStore } from '@/client/client-store';
import { ensureRoleScreens, isSideNavItemAllowed } from '@/auth/role-screen-access';

// `@ey-xd/motif-components` types `IconFunction` as `(...) => HTMLElement & SVGElement`,
// but the actual return is just `SVGElement`. Bridge type that matches the
// Motif element prop without lying about the runtime shape.
type MotifIconFn = (props: {
  size?: string;
  fill?: string;
  strokeWidth?: string;
  title?: string;
  stroke?: string;
}) => HTMLElement & SVGElement;

/**
 * Force a literal white `fill` onto an Iconoir icon function.
 *
 * `MotifVerticalNavigationMenuItem` renders the icon through an internal
 * `<motif-icon src={fn} />` we can't hand props to. motif-icon invokes the
 * icon function with `fill: 'var(--icon-icon-color)'` and writes that onto the
 * generated SVG's `fill` *presentation attribute* — but `var()` does NOT
 * resolve inside an SVG presentation attribute in Chromium, so `fill` falls
 * back to its initial value (black) and the icon is invisible on the dark
 * `#2e2e38` rail. Wrapping the function to override `fill` with a literal hex
 * paints the paths white directly (legacy `ey-global-navigation-rail` parity).
 */
function whiteIcon(fn: MotifIconFn): MotifIconFn {
  return (props) => fn({ ...props, fill: '#ffffff' });
}

interface NavItem {
  label: string;
  path: string;
  /**
   * Role-screen appId used to gate VISIBILITY against the user's role-screen
   * mapping — the same key {@link isScreenAllowed} checks in the route guard.
   * Keeping the rail permission-driven (legacy `SideNavigationService.getMenu`)
   * means a user only sees items they can actually open, so clicking one can no
   * longer be bounced to Home by `validateScreenAccess` (Bug #1–#4). `home` and
   * `workspace` are shell-essential and always allowed.
   */
  appId: string;
  icon: MotifIconFn;
}

/**
 * Primary navigation items mirroring the legacy `app-side-navigation`
 * rail (`side-navigation.component.html`). The legacy shell loads the
 * active tier from `SideNavigationService.getMenu()` which is driven by
 * RUM/permission config; we ship a curated set matching the legacy
 * portal's primary apps and wire fully permission-driven items in M3.1
 * alongside the Workspaces panel. Each app id resolves to its legacy MFE
 * via the `/app/$appId` route (proxied `/static/{appId}` bundle); `Home`
 * targets the root route (home MFE) and `Apps` opens the React launcher.
 */
const ITEMS: NavItem[] = [
  {
    label: 'Home',
    path: '/',
    appId: 'home',
    icon: whiteIcon(IconoirHomeSimple as unknown as MotifIconFn),
  },
  {
    label: 'Work Items',
    path: '/app/workitemuilist',
    appId: 'workitemuilist',
    icon: whiteIcon(IconoirTaskList as unknown as MotifIconFn),
  },
  {
    label: 'Document Center',
    path: '/app/doccenter',
    appId: 'doccenter',
    icon: whiteIcon(IconoirMultiplePages as unknown as MotifIconFn),
  },
  {
    label: 'Dashboards',
    path: '/app/dashboards',
    appId: 'dashboards',
    icon: whiteIcon(IconoirStatsReport as unknown as MotifIconFn),
  },
  {
    label: 'Data',
    path: '/app/pie',
    appId: 'pie',
    icon: whiteIcon(IconoirDatabase as unknown as MotifIconFn),
  },
  {
    label: 'Apps',
    path: '/apps',
    appId: 'workspace',
    icon: whiteIcon(IconoirViewGrid as unknown as MotifIconFn),
  },
  {
    // Legacy `administration-nav.config.ts`: the last rail item is "Admin"
    // (icon "settings", order 7), NOT a bespoke shell settings page. It mounts
    // the admin MFE, which renders the per-client admin tiles
    // (`/portaladmin/api/v1/Admin/GetTiles`). The shell `/settings` page stays
    // reachable from the user panel's "Notification Preferences" link.
    label: 'Admin',
    path: '/app/admin',
    appId: 'admin',
    icon: whiteIcon(IconoirSettings as unknown as MotifIconFn),
  },
];

/**
 * appId → menu label, the single source of truth the browser-tab title
 * (`useDocumentTitle`) reuses so a routed app's tab name matches its nav label
 * (e.g. `workitemuilist` → "Work Items"). Derived from {@link ITEMS} to avoid a
 * second hand-maintained list drifting out of sync.
 */
export const NAV_LABEL_BY_APP_ID: Readonly<Record<string, string>> = Object.fromEntries(
  ITEMS.map((item) => [item.appId, item.label]),
);

interface SideNavRailProps {
  /**
   * When true the rail expands to the wide labeled panel (legacy `Expanded`);
   * otherwise it stays the 60px icon strip (`Collapsed`). Owned by ShellLayout.
   */
  expanded?: boolean;
}

/**
 * Vertical navigation rail. Mounted at the left edge of `ShellLayout`, it
 * mirrors the legacy `ey-global-navigation-rail`'s two visible states:
 *   - Collapsed: fixed 60px icon strip (default resting state).
 *   - Expanded:  wide panel with icon + text labels, overlaying page content
 *     (the legacy rail's built-in collapse button, `isCollapseButtonHidden=false`).
 * The bottom chevron toggles Collapsed<->Expanded; the header hamburger
 * hides/shows the whole rail (legacy `Closed`).
 */
export function SideNavRail({ expanded = false }: SideNavRailProps) {
  const router = useRouter();
  const location = useRouterState({ select: (s) => s.location });
  const collapsed = !expanded;
  const toggleLabel = expanded ? 'Collapse navigation' : 'Expand navigation';
  const [tooltip, setTooltip] = useState<{ label: string; top: number } | null>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  // Permission-driven visibility (legacy `SideNavigationService.getMenu`): keep
  // only items the user's role-screen mapping allows, so a shown item is always
  // openable and can't be bounced to Home by `validateScreenAccess` (Bug #1–#4).
  // Reuses the guard's cached fetch (`ensureRoleScreens`) — no extra request.
  const currentClientId = useClientStore((s) => s.current?.id);
  const { data: screens } = useQuery({
    queryKey: ['role-screens', currentClientId ?? null],
    queryFn: () => ensureRoleScreens(currentClientId as string),
    enabled: Boolean(currentClientId),
    staleTime: 5 * 60_000,
  });

  // Degrade gracefully: until the mapping resolves (or if it comes back empty /
  // errors) show the full curated set rather than hiding everything — matches
  // the guard, which also does not block on an empty/failed mapping.
  const items = useMemo(
    () =>
      screens && screens.length > 0
        ? ITEMS.filter((i) => isSideNavItemAllowed(i.appId, screens))
        : ITEMS,
    [screens],
  );

  const activeIndex = items.findIndex((i) =>
    i.path === '/' ? location.pathname === '/' : location.pathname.startsWith(i.path),
  );

  const showTooltipForEl = useCallback(
    (el: HTMLElement, idx: number) => {
      if (!collapsed) return;
      const rect = el.getBoundingClientRect();
      setTooltip({ label: items[idx].label, top: rect.top + rect.height / 2 });
    },
    [collapsed, items],
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  // Patch the motif-vertical-navigation element once it hydrates:
  //  1. Disable Motif's built-in expand-on-hover. Internally the component's
  //     shadow <nav> has `onMouseEnter` -> `handleMouseEnter` which sets
  //     `this.hovering = true`; the `@Watch('hovering')` (`hoveringChanged`)
  //     then forces `this.collapsed = false`, expanding the rail. We keep the
  //     rail controlled solely by the chevron/hamburger, so we LOCK `hovering`
  //     to `false` on the instance: Motif's `this.hovering = true` becomes a
  //     no-op, the watcher never fires, and the rail can never self-expand.
  //     (Capturing the shadow <nav>'s mouseenter/mouseleave is kept as a
  //     belt-and-suspenders fallback in case the property lock is rejected.)
  //  2. Hide the scrollbar on .motif-vertical-navigation-scrolls + strip the
  //     content top padding (both shadow-internal, unreachable via outer CSS).
  const navPatched = useRef(false);
  const hoverLocked = useRef(false);
  useEffect(() => {
    if (navPatched.current) return;
    const stopHoverExpand = (e: Event) => e.stopImmediatePropagation();
    let shadow: ShadowRoot | null = null;
    const lockHovering = (navEl: Element) => {
      if (hoverLocked.current) return;
      try {
        Object.defineProperty(navEl, 'hovering', {
          configurable: true,
          get: () => false,
          set: () => {},
        });
        hoverLocked.current = true;
      } catch {
        // Non-configurable accessor: rely on the mouseenter capture fallback.
      }
    };
    const patch = () => {
      const navEl = document.querySelector('.shell-side-rail motif-vertical-navigation');
      if (!navEl) return false;
      lockHovering(navEl);
      shadow = navEl.shadowRoot ?? null;
      if (!shadow) return false;
      const style = document.createElement('style');
      style.textContent = `.motif-vertical-navigation-scrolls {
        overflow: hidden !important;
        scrollbar-width: none !important;
      }
      .motif-vertical-navigation-scrolls::-webkit-scrollbar {
        display: none !important;
      }
      .motif-vertical-navigation-content {
        padding-top: 0 !important;
      }`;
      shadow.appendChild(style);
      shadow.addEventListener('mouseenter', stopHoverExpand, true);
      shadow.addEventListener('mouseleave', stopHoverExpand, true);
      navPatched.current = true;
      return true;
    };
    const detach = () => {
      shadow?.removeEventListener('mouseenter', stopHoverExpand, true);
      shadow?.removeEventListener('mouseleave', stopHoverExpand, true);
    };
    if (patch()) return detach;
    // Shadow root may not be ready yet; retry after the web component hydrates.
    const timer = setInterval(() => {
      if (patch()) clearInterval(timer);
    }, 100);
    return () => {
      clearInterval(timer);
      detach();
    };
  }, []);

  // Tooltip via event delegation: `mouseover`/`mouseout` are composed events
  // that bubble through shadow DOM boundaries, unlike `mouseenter`/`mouseleave`.
  // We attach a single listener pair on the nav container and walk up from the
  // composed target to find which menu-item host is being hovered.
  const navContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!collapsed) {
      setTooltip(null);
      return;
    }
    const container = navContainerRef.current;
    if (!container) return;

    const findMenuItem = (target: EventTarget | null): { el: HTMLElement; idx: number } | null => {
      let node = target as Node | null;
      while (node && node !== container) {
        if (
          node instanceof HTMLElement &&
          node.tagName.toLowerCase().includes('vertical-navigation-menu-item')
        ) {
          const idx = itemRefs.current.indexOf(node);
          if (idx >= 0) return { el: node, idx };
        }
        // Cross shadow DOM boundaries by jumping to the host element
        if (node instanceof ShadowRoot) {
          node = node.host;
        } else {
          node = node.parentNode;
        }
      }
      return null;
    };

    const onOver = (e: MouseEvent) => {
      const target = e.composedPath?.()[0] ?? e.target;
      const match = findMenuItem(target);
      if (match) showTooltipForEl(match.el, match.idx);
    };
    const onOut = (e: MouseEvent) => {
      // Only hide if pointer is leaving to an element outside the current item
      const relatedMatch = findMenuItem(e.relatedTarget as Node | null);
      const currentMatch = findMenuItem(e.composedPath?.()[0] ?? e.target);
      if (!relatedMatch || relatedMatch.idx !== currentMatch?.idx) hideTooltip();
    };

    container.addEventListener('mouseover', onOver);
    container.addEventListener('mouseout', onOut);
    return () => {
      container.removeEventListener('mouseover', onOver);
      container.removeEventListener('mouseout', onOut);
    };
  }, [collapsed, showTooltipForEl, hideTooltip]);

  return (
    <div ref={navContainerRef} style={{ height: '100%' }}>
      <MotifVerticalNavigation verticalNavigationAriaLabel="Primary" collapsed={collapsed}>
        <MotifVerticalNavigationMenu
          slot="verticalNavigationContent"
          collapsed={collapsed}
          activeIndex={activeIndex < 0 ? null : activeIndex}
        >
          {items.map((item, idx) => (
            <MotifVerticalNavigationMenuItem
              key={item.path}
              ref={(el: HTMLElement | null) => {
                itemRefs.current[idx] = el;
              }}
              slot="verticalNavigationMenuElements"
              label={item.label}
              icon={item.icon}
              collapsed={collapsed}
              selected={idx === activeIndex}
              onClick={() => {

                void router.navigate({ to: item.path }).catch((e: unknown) => {
                  const err = e instanceof Error ? e : new Error(String(e));
                  getTracker().error(err, { stage: 'side_nav_navigate', path: item.path });
                });
              }}
            >
              <span slot="verticalNavigationMenuItemElements">{item.label}</span>
            </MotifVerticalNavigationMenuItem>
          ))}
        </MotifVerticalNavigationMenu>
        <button
          slot="verticalNavigationFooter"
          type="button"
          className="shell-side-rail__bottom-toggle"
          aria-label={toggleLabel}
          aria-expanded={expanded}
          title={toggleLabel}
          onClick={() => {

            eventsModule.emit('shell:expand-sidenav');
          }}
        >
          <MotifIcon
            src={expanded ? IconoirNavArrowLeft : IconoirNavArrowRight}
            iconAriaLabel={toggleLabel}
          />
        </button>
      </MotifVerticalNavigation>
      {collapsed && tooltip && (
        <div className="shell-side-rail__hover-tooltip" style={{ top: tooltip.top }}>
          {tooltip.label}
        </div>
      )}
    </div>
  );
}
